
import React from 'react';
import FeatureVideo from './FeatureVideo';
import { Bot, FileText, Share, MessageSquare, Sparkles } from 'lucide-react';
import { useBreakpoint } from '@/hooks/use-mobile';
import { Link } from 'react-router-dom';
import CTAButton from '../CTAButton';
import { SectionContainer } from '../section/SectionContainer';

const AIAssistantsSection = () => {
  const { isMobile } = useBreakpoint();
  
  const features = [
    {
      icon: <FileText size={isMobile ? 16 : 20} className="text-[#003a70]" />,
      title: "Custom Knowledge Base",
      description: "Train AI with your content, documents, and FAQs."
    },
    {
      icon: <Share size={isMobile ? 16 : 20} className="text-[#003a70]" />,
      title: "Deploy Anywhere",
      description: "Embed AI on websites, chat platforms, or direct links."
    },
    {
      icon: <Bot size={isMobile ? 16 : 20} className="text-[#003a70]" />,
      title: "Multi-Model AI Support",
      description: "Use GPT-4, Claude, and Gemini AI models."
    },
    {
      icon: <MessageSquare size={isMobile ? 16 : 20} className="text-[#003a70]" />,
      title: "Real-Time Learning & Context Retention",
      description: "AI adapts and improves responses."
    }
  ];

  return (
    <SectionContainer containerWidth="wide" className="relative z-10">
      {/* Header Section */}
      <div className="max-w-6xl mx-auto mb-16 md:mb-20 px-4">
        <div className="relative">
          {/* Background decorative element */}
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#FEE6D8]/30 rounded-full blur-3xl -z-10"></div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#003a70]/10 rounded-full blur-3xl -z-10"></div>
          
          {/* Content wrapper with gradient border */}
          <div className="relative border border-gradient-to-r from-[#FEE6D8] to-[#003a70]/20 rounded-2xl bg-white/70 backdrop-blur-sm p-8 md:p-10 shadow-xl">
            {/* Badge with animated sparkle effect */}
            <div className="flex justify-center mb-6">
              <div className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/60 text-[#003a70] text-sm font-medium shadow-sm transform hover:scale-105 transition-all duration-300">
                <Sparkles className="h-4 w-4 animate-pulse" />
                <span className="relative z-10 font-semibold tracking-wide">AI ASSISTANTS</span>
              </div>
            </div>
            
            {/* Two-part heading with underline accent */}
            <h2 className="text-center">
              <span className="block text-3xl md:text-4xl lg:text-5xl font-bold text-[#003a70] mb-3 leading-tight">
                Everything You Need to 
              </span>
              <span className="relative inline-block text-2xl md:text-3xl lg:text-4xl font-bold text-[#003a70]/80">
                Build Smart AI Tools Fast
                <span className="absolute -bottom-2 left-0 w-full h-1.5 bg-[#FEE6D8] rounded-full transform -skew-x-6"></span>
              </span>
            </h2>
            
            {/* Enhanced description */}
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mt-8 text-center leading-relaxed">
              Create fully-customized AI chatbots and assistants that can answer questions, complete tasks, and seamlessly integrate with your existing tools and data sources.
            </p>
          </div>
        </div>
      </div>

      {/* Video Section */}
      <FeatureVideo 
        section="ai-assistants"
      />

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 px-4 lg:px-0">
        {features.map((feature, index) => (
          <div 
            key={index} 
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100 hover:border-[#FEE6D8]"
          >
            <div className="bg-[#FEE6D8] p-3 rounded-lg inline-block mb-4">
              <div className="text-[#003a70]">
                {feature.icon}
              </div>
            </div>
            <h3 className="text-lg font-semibold text-[#003a70] mb-2">{feature.title}</h3>
            <p className="text-gray-600">{feature.description}</p>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <Link to="/pricing">
          <CTAButton 
            variant="primary" 
            className="group text-lg px-8 py-4"
          >
            Try AI Assistants Now
          </CTAButton>
        </Link>
      </div>
    </SectionContainer>
  );
};

export default AIAssistantsSection;
